import { Fragment, type HTMLAttributes } from 'react';
import { computeDistanceBetween } from 'spherical-geometry-js';

import { IconLocationPinOutline } from '@virginexperiencedays/components-v2/src/icons/IconLocationPin';
import { IconVedTick } from '@virginexperiencedays/components-v2/src/icons/IconVedTick';
import { IconInformationCircle } from '@virginexperiencedays/components-v2/src/icons/IconInformationCircle';
import { Location } from '@virginexperiencedays/products';
import { cn } from '@virginexperiencedays/tailwind';

import { titleCase } from '../../../../utils/titleCase';

export type LocationsItineraryProps = {
  locations?: Location[];
  locationsTitles?: string[];
} & HTMLAttributes<HTMLDivElement>;

type Coordinates = { lat: number; lng: number };

export const LocationsItinerary = ({
  className,
  locations,
  locationsTitles,
  ...rest
}: LocationsItineraryProps) => {
  const locationsCoordinates: Coordinates[] = locations?.map(({ lat, lng }) => ({
    lat: isNaN(Number(lat)) ? 0 : Number(lat),
    lng: isNaN(Number(lng)) ? 0 : Number(lng),
  }));

  const titles = locationsTitles?.slice(0, locationsCoordinates.length);

  if (!locations?.length || !locationsTitles?.length) return null;

  return (
    <section
      className={cn('flex w-full flex-col gap-y-6', className)}
      aria-label="Locations itinerary"
      {...rest}
    >
      <div className="relative flex h-min flex-col gap-y-4 before:absolute before:left-3 before:top-0 before:-z-[1] before:h-full before:w-2 before:rounded-sm before:bg-primary-100">
        {titles?.map((title, i) => (
          <Fragment key={`${title}-${i}`}>
            <div className="relative flex gap-x-4 before:absolute before:left-0 before:-z-[1] before:hidden before:w-32 before:bg-background-page first:before:top-0 first:before:block first:before:h-1/4 last:before:bottom-0 last:before:block last:before:h-full">
              <div className="flex size-[32px] shrink-0 items-center justify-center rounded-full bg-brand shadow-[0px_3.2px_4.8px_-0.8px_#0000001A,0px_1.6px_3.2px_2.4px_#0000000F]">
                <IconVedTick className="h-[22px] w-[11px]" />
              </div>

              <h3 className="font-inter text-base font-medium">{titleCase(title)}</h3>
            </div>

            {i !== titles.length - 1 && (
              <LocationsDistance
                coordinates={[locationsCoordinates[i], locationsCoordinates[i + 1]]}
              />
            )}
          </Fragment>
        ))}
      </div>

      <div className="flex gap-x-2 text-xs leading-[18px] text-grey-500">
        <IconInformationCircle className="size-5" />
        <p>
          <span className="font-semibold">Note:</span> Distances are measured in a straight line
          from each location
        </p>
      </div>
    </section>
  );
};

const CONVERSION_FACTOR_M_TO_MILES = 0.000621371192;

type LocationsDistanceProps = {
  coordinates: [Coordinates, Coordinates];
};

const LocationsDistance = ({ coordinates }: LocationsDistanceProps) => {
  const distance = Number(
    (computeDistanceBetween(...coordinates) * CONVERSION_FACTOR_M_TO_MILES).toFixed(2)
  );

  if (distance === undefined) return null;

  return (
    <div className="flex items-center gap-x-4">
      <div className="flex size-[32px] items-center justify-center rounded-full bg-white shadow-[0px_3.2px_4.8px_-0.8px_#0000001A,0px_1.6px_3.2px_2.4px_#0000000F]">
        <IconLocationPinOutline stroke="var(--text-brand)" />
      </div>

      <p>
        <span className="inline-block whitespace-pre-wrap font-medium after:content-['_']">
          {distance} mile{distance === 1 ? '' : 's'}
        </span>
        <span className="text-xs leading-[18px] text-grey-500">(approx)</span>
      </p>
    </div>
  );
};
